import React, { useState, useEffect } from "react";
import FrenchDatePicker from "../ui/FrenchDatePicker";
import api from "../../api/axios";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import FullCalendar from "./FullCalendar";
import Input from "../ui/Input";
import Button from "../ui/Button";
import ModelSelect from "../ui/ModelSelect";

const planningFieldLabels = {
  model_id: "Modèle",
  chain: "Chaîne",
  date_etude: "Date d’étude",
  start_date: "Date de début",
  end_date: "Date de fin",
  qte: "Quantité Société",
  cours_devise_etude: "Cours de la devise",
  main_oeuvre_directe_gamme: "Main-d’œuvre directe / Gamme",
  main_oeuvre_directe_support: "Main-d’œuvre directe / Support",
  total_main_oeuvre_directe_affectee: "Total de la main-d’œuvre directe affectée",
  rendement_prevu_gamme: "Rendement prévu par la gamme",
  temps_alloue: "Temps alloué",
  file: "Fichier Excel",
};

const getSubmissionErrors = (requestError) => {
  const validationErrors = requestError.response?.data?.errors;
  if (validationErrors && typeof validationErrors === "object") {
    return Object.entries(validationErrors).map(([field, messages]) => {
      const label = planningFieldLabels[field] || field;
      const message = Array.isArray(messages) ? messages[0] : messages;

      if (/required/i.test(message)) return `${label} est obligatoire.`;
      if (/integer/i.test(message)) return `${label} doit être un nombre entier.`;
      if (/numeric|number/i.test(message)) return `${label} doit être un nombre valide.`;
      if (/at least|minimum|min/i.test(message)) return `${label} ne peut pas être négatif.`;
      if (/date/i.test(message)) return `${label} doit contenir une date valide.`;
      if (/mimes|file type/i.test(message)) return `${label} doit être au format .xls ou .xlsx.`;

      return `${label} : ${message}`;
    });
  }

  const message = requestError.response?.data?.message;
  if (requestError.response?.status === 409) {
    return ["Une planification existe déjà pour ce modèle et cette chaîne. Utilisez le bouton Modifier."];
  }
  return [message || "Une erreur inattendue est survenue. Veuillez réessayer."];
};

function ProductPlanning() {
  const [data, setData] = useState({
    model_id: "",
    chain: "",
    date_etude: null,
    cours_devise_etude: null,
    start_date: null,
    end_date: null,
    qte: "",
    temps_alloue: "",
    main_oeuvre_directe_gamme: "",
    main_oeuvre_directe_support: "",
    total_main_oeuvre_directe_affectee: "",
    rendement_prevu_gamme: "",
  });
  const [models, setModels] = useState([]);
  const [chains, setChains] = useState([]);
  const [planningData, setPlanningData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submissionErrors, setSubmissionErrors] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [planningDataId, setPlanningDataId] = useState(null);
  const [systemConstant, setSystemConstant] = useState({
    Nombre_d_heures_par_jour: "",
    Taux_horaire_SMIG_16_29: "",
    Taux_horaire_17_00: "",
    Taux_horaire_17_50: "",
    Masse_salariale_16_29: "",
    Masse_salariale_17_00: "",
    Masse_salariale_17_50: "",
    Capacité_par_unité_transport: "",
    cotisation_entroprise_trans: "",
    Coût_par_trajet: "",
    Coût_énergie_journalier: "",
    Coût_charges_fixes_journalier: "",
  });
  const [totalChains, setTotalChains] = useState(0);
  const [effectifInDirect, setEffectifInDirect] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [fils, setFils] = useState(0);
  const [plastique, setPlastique] = useState(0);
  const [totalExport, setTotalExport] = useState(0);
  const [effectifDirect, setEffectifDirect] = useState(0);
  const [modalMessage, setModalMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [taux , setTaux] = useState(null);
  const [file, setFile] = useState(null);

  useEffect(() => {
    const selectedModel = models.find(
      (model) => String(model.id) === String(data.model_id)
    );
    const currency = selectedModel?.devise?.toUpperCase();

    if (!data.date_etude || !currency) {
      setData((previous) => ({
        ...previous,
        cours_devise_etude: null,
      }));
      return;
    }

    if (currency === "MAD" || currency === "DH") {
      setData((previous) => ({
        ...previous,
        cours_devise_etude: 1,
      }));
      return;
    }

    let cancelled = false;

    const fetchStudyExchangeRate = async () => {
      try {
        const response = await fetch(
          `https://v6.exchangerate-api.com/v6/6610059cf4c8cb5955adc828/latest/${currency}`
        );
        if (!response.ok) {
          throw new Error(`Statut HTTP ${response.status}`);
        }

        const exchangeData = await response.json();
        const rate = exchangeData.conversion_rates?.MAD;
        if (!cancelled && typeof rate === "number") {
          setData((previous) => ({
            ...previous,
            cours_devise_etude: rate,
          }));
        }
      } catch (exchangeError) {
        if (!cancelled) {
          setData((previous) => ({
            ...previous,
            cours_devise_etude: null,
          }));
          toast.error("Impossible de récupérer le cours de la devise d'étude.");
        }
      }
    };

    fetchStudyExchangeRate();

    return () => {
      cancelled = true;
    };
  }, [data.date_etude, data.model_id, models]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      setFile(null);
      return;
    }
    const extension = selectedFile.name.split(".").pop()?.toLowerCase();
    if (!["xls", "xlsx"].includes(extension)) {
      event.target.value = "";
      setFile(null);
      toast.error("Le fichier de planification doit être au format Excel (.xls ou .xlsx). ");
      return;
    }
    setFile(selectedFile);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const responseChains = await api.get(`/total_chain`);
        const responseIndirect = await api.get(`/effective_indirect_total`);
        const systemResponse = await api.get("/system_constants_latest");
        if(data.model_id) {
          const responseDirect = await api.get(
            `/effective_direct_standard/${data.model_id}`
          );
          setEffectifDirect(parseInt(responseDirect.data.total_effectif_direct));
        }

        if (systemResponse.data) {
          const { id, created_at, updated_at, effectif_fix, ...filteredData } =
            systemResponse.data;
          setSystemConstant({
            ...filteredData,
          });
        }

        setEffectifInDirect(
          parseInt(responseIndirect.data.total_effectif_indirect)
        );
        setTotalChains(parseInt(responseChains.data.total_chains));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }

    fetchData();
  }, [data.model_id ])

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await api.get("/models");
        setModels(response.data);
      } catch (error) {
        toast.error("Erreur lors de la récupération des modèles.");
      }
    };

    const fetchChains = async () => {
      try {
        const response = await api.get("/chains");
        setChains(response.data);
      } catch (error) {
        toast.error("Erreur lors de la récupération des chaînes.");
      }
    };

    fetchModels();
    fetchChains();
  }, []);

  useEffect(() => {
    const fetchPlanning = async () => {
      if (data.model_id && data.chain) {
        setLoading(true);
        try {
          const response = await api.get(
            `/product_plans_model/${data.model_id}/${data.chain}`
          );
          console.log(response)
          setPlanningDataId(response.data.id);
          setPlanningData({
             ...response.data,
             file_path: `https://api.crosscheckconf.net/storage/${response.data.file_path?.replace("public/", "")}`
         });

        } catch (error) {
          if (error.response && error.response.status === 404) {
            setPlanningData(null);
          } else {
            toast.error(
              "Erreur lors de la récupération des données de planification."
            );
          }
        } finally {
          setLoading(false);
        }
      }
    };

    if (!isEditing) {
      if (data.model_id && data.chain) {
        fetchPlanning();
      }
    }
  }, [data.model_id, data.chain, models , isEditing]);

  const handleSubmit = async (event) => {
    setSubLoading(true);
    event.preventDefault();
    setSubmissionErrors([]);

    if (!data.date_etude || !data.start_date || !data.end_date) {
      const messages = ["Les trois dates — étude, début et fin — sont obligatoires."];
      setSubmissionErrors(messages);
      toast.error(messages[0]);
      setSubLoading(false);
      return;
    }

    if (data.cours_devise_etude === null) {
      const messages = ["Le cours de la devise n’est pas encore disponible. Patientez quelques instants, puis réessayez."];
      setSubmissionErrors(messages);
      toast.error(messages[0]);
      setSubLoading(false);
      return;
    }

    const formattedData = {
      ...data,
      start_date: data.start_date
        ? new Date(data.start_date).toISOString().split("T")[0]
        : null,
      end_date: data.end_date
        ? new Date(data.end_date).toISOString().split("T")[0]
        : null,
      date_etude: data.date_etude
        ? new Date(data.date_etude).toISOString().split("T")[0]
        : null,
    };

    const formData = new FormData();
    Object.keys(formattedData).forEach((key) => {
      formData.append(key, formattedData[key]);
    });
    if (file) {
      formData.append("file", file);
    }
  
  

    try {
      await api.post("/product_plans", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setSubmissionErrors([]);
      toast.success("Planification de produit créée avec succès.");
    } catch (error) {
      const messages = getSubmissionErrors(error);
      setSubmissionErrors(messages);
      toast.error("La planification n’a pas pu être créée. Vérifiez les champs signalés.");
    } finally {
      setSubLoading(false);
    }
  };

  const handleUpdateSubmit = async (event) => {
    setSubLoading(true);
    event.preventDefault();
    setSubmissionErrors([]);

    if (!data.date_etude || !data.start_date || !data.end_date) {
      const messages = ["Les trois dates — étude, début et fin — sont obligatoires."];
      setSubmissionErrors(messages);
      toast.error(messages[0]);
      setSubLoading(false);
      return;
    }

    if (data.cours_devise_etude === null) {
      const messages = ["Le cours de la devise n’est pas encore disponible. Patientez quelques instants, puis réessayez."];
      setSubmissionErrors(messages);
      toast.error(messages[0]);
      setSubLoading(false);
      return;
    }

    const formattedData = {
      ...data,
      start_date: data.start_date
        ? new Date(data.start_date).toISOString().split("T")[0]
        : null,
      end_date: data.end_date
        ? new Date(data.end_date).toISOString().split("T")[0]
        : null,
      date_etude: data.date_etude
        ? new Date(data.date_etude).toISOString().split("T")[0]
        : null,
    };
    const formData = new FormData();
    Object.keys(formattedData).forEach((key) => {
      formData.append(key, formattedData[key]);
    });
    if (file) {
      formData.append("file", file);
    }
  
    try {
      await api.post(`/product_plans/${data.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    
      setIsEditing(false);
      setPlanningData(null); 
      setSubmissionErrors([]);
      toast.success("Planification modifiée avec succès.");
    } catch (error) {
      const messages = getSubmissionErrors(error);
      setSubmissionErrors(messages);
      toast.error("La planification n’a pas pu être modifiée. Vérifiez les champs signalés.");
    } finally {
      setSubLoading(false);
    }
  };

    const resetForm = () => {
    setData({
      start_date: null,
      end_date: null,
      date_etude: null,
      cours_devise_etude: null,
      qte: "",
      temps_alloue: "",
      main_oeuvre_directe_gamme: "",
      main_oeuvre_directe_support: "",
      total_main_oeuvre_directe_affectee: "",
      rendement_prevu_gamme: "",
    });
    const fetchPlanning = async () => {
      if (data.model_id && data.chain) {
        setLoading(true);
        try {
          const response = await api.get(
            `/product_plans_model/${data.model_id}/${data.chain}`
          );
          setPlanningDataId(response.data.id)
          setPlanningData({
            ...response.data,
            file_path: `https://api.crosscheckconf.net/storage/${response.data.file_path.replace("public/", "")}`
        });

        } catch (error) {
          if (error.response && error.response.status === 404) {
            setPlanningData(null);
          } else {
            toast.error(
              "Erreur lors de la récupération des données de planification."
            );
          }
        } finally {
          setLoading(false);
        }
      }
    };

    if (!isEditing) {
      if (data.model_id && data.chain) {
        fetchPlanning();
      }
    }
    setPlanningData(null);
  };


  const onChange = (name, value) => {
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleDateChange = (date, name) => {
    setData({
      ...data,
      [name]: date,
    });
  };

  const handleEdit = async () => {
    try {
      setPlanningData(null);
      setIsEditing(true);
      const response = await api.get(`/product_plans_single/${planningDataId}`);
      const fetchedData = response.data;
      setData({
        ...fetchedData,
        start_date: fetchedData.start_date
          ? new Date(fetchedData.start_date)
          : null,
        end_date: fetchedData.end_date ? new Date(fetchedData.end_date) : null,
        date_etude: fetchedData.date_etude
          ? new Date(fetchedData.date_etude)
          : null,
      });
    } catch (error) {
      toast.error(
        "Erreur lors de la récupération des données de planification."
      );
    } finally {
      setPlanningDataId(null);
    }
  };

  const handleDelete = async () => {
    if (planningData) {
      setShowDeleteModal(true);
    }
  };

  const confirmDelete = async () => {
    if (planningData) {
      try {
        await api.delete(`/product_plans/${planningData.id}`);
        resetForm();
        toast.success("Plan supprimé avec succès.");
      } catch (error) {
        toast.error("Erreur lors de la suppression du plan.");
      } finally {
        setShowDeleteModal(false);
      }
    }
  };

  const fetchWorkedHours = async (modelId) => {
    try {
      const response = await api.get(`/production_real_hours/${modelId}`);
      setTotalHours(response.data.worked_hours_count);
    } catch (error) {
      console.error("Error fetching worked hours:", error);
      setError(error.response?.data?.message || "An error occurred");
    }
  };

  const calculateCouteFilAndPlastique = () => {
    if(planningData) {
      setFils(planningData.consummation_standard_fil);
      setPlastique(planningData.consummation_standard_plastique);
    }
  };

  const fetchData = async (modelId) => {
    try {
      const response = await api.get(`/export/${modelId}`);
      const exportData = response.data;
      setTotalExport(exportData.total);
    } catch (error) {
      console.error(error);
    }
  };


  useEffect(() => {
    if (data.model_id) {
      fetchWorkedHours(data.model_id);
      fetchData(data.model_id);
    }
  }, [data.model_id]);

  useEffect(() => {
    if (
      totalHours > 0 &&
      effectifDirect > 0 &&
      effectifInDirect > 0 &&
      totalChains > 0 &&
      systemConstant.Nombre_d_heures_par_jour
    ) {
      calculateCouteFilAndPlastique();
    }
  }, [
    totalHours,
    effectifDirect,
    effectifInDirect,
    totalChains,
    totalExport,
    systemConstant,
  ]);


  const handleTest = () => {
    console.log("🔍 Initial Values:");
    console.log("totalHours:", totalHours);
    console.log("effectifDirect:", effectifDirect);
    console.log("effectifInDirect:", effectifInDirect);
    console.log("totalChains:", totalChains);
    console.log("systemConstant:", systemConstant);
  
    if (
      totalHours > 0 &&
      effectifDirect > 0 &&
      effectifInDirect > 0 &&
      totalChains > 0 &&
      systemConstant.Nombre_d_heures_par_jour
    ) {
      const {
        Nombre_d_heures_par_jour,
        Capacité_par_unité_transport,
        Coût_par_trajet,
        cotisation_entroprise_trans,
        Taux_horaire_SMIG_16_29,
        Taux_horaire_17_00,
        Taux_horaire_17_50,
        Coût_charges_fixes_journalier,
        Coût_énergie_journalier,
        Masse_salariale_16_29,
        Masse_salariale_17_00,
        Masse_salariale_17_50
      } = systemConstant;
  
      const QP_effectif = effectifInDirect / totalChains;
      const Nombre_de_jours_de_production = totalHours / Nombre_d_heures_par_jour;
      const Cles_de_repartition_charges_indirectes = totalHours / (Nombre_d_heures_par_jour * totalChains);
      const coûtChargesFixes = Coût_charges_fixes_journalier * Nombre_de_jours_de_production * Cles_de_repartition_charges_indirectes;
      const coûtAchatsConsommesJournalier = (parseFloat(fils) + parseFloat(plastique)) / Nombre_de_jours_de_production;
      const coûtAchatPlastiqueFils = Nombre_de_jours_de_production * coûtAchatsConsommesJournalier;
      const coûtEnergie = Coût_énergie_journalier * Nombre_de_jours_de_production * Cles_de_repartition_charges_indirectes;
  
      console.log("🧮 Calculated Intermediate Values:");
      console.log("QP_effectif:", QP_effectif);
      console.log("Nombre_de_jours_de_production:", Nombre_de_jours_de_production);
      console.log("coûtChargesFixes:", coûtChargesFixes);
      console.log("coûtAchatsConsommesJournalier:", coûtAchatsConsommesJournalier);
      console.log("coûtAchatPlastiqueFils:", coûtAchatPlastiqueFils);
      console.log("coûtEnergie:", coûtEnergie);
  
      const Nombre_unite_utilise = (QP_effectif + effectifDirect) / Capacité_par_unité_transport;
      const Refacturation_transport_MOD_indirecte = -((cotisation_entroprise_trans * Nombre_de_jours_de_production * QP_effectif) / 26);
      const Refacturation_transport_MOD_directe = -((cotisation_entroprise_trans * Nombre_de_jours_de_production * effectifDirect) / 26);
      const transportCost = (2 * Nombre_unite_utilise * Nombre_de_jours_de_production * Coût_par_trajet) + (Refacturation_transport_MOD_directe + Refacturation_transport_MOD_indirecte);
  
      const Nombre_heures_travaillees_direct = totalHours * effectifDirect;
      const Nombre_heures_travaillees_indirect = totalHours * QP_effectif;
  
      const conge_direct = (1.5 * Nombre_heures_travaillees_direct * Taux_horaire_SMIG_16_29) / 26;
      const conge_indirect = (1.5 * Nombre_heures_travaillees_indirect * Taux_horaire_SMIG_16_29) / 26;
  
      const masseSalarialeDirect =
        (Nombre_heures_travaillees_direct * Masse_salariale_16_29 * 0.01 * Taux_horaire_SMIG_16_29) +
        (Nombre_heures_travaillees_direct * Masse_salariale_17_00 * 0.01 * Taux_horaire_17_00) +
        (Nombre_heures_travaillees_direct * Masse_salariale_17_50 * 0.01 * Taux_horaire_17_50) +
        conge_direct;
  
      const masseSalarialeIndirect =
        (Nombre_heures_travaillees_indirect * Masse_salariale_16_29 * 0.01 * Taux_horaire_SMIG_16_29) +
        (Nombre_heures_travaillees_indirect * Masse_salariale_17_00 * 0.01 * Taux_horaire_17_00) +
        (Nombre_heures_travaillees_indirect * Masse_salariale_17_50 * 0.01 * Taux_horaire_17_50) +
        conge_indirect;
  
      const totalMassSalary = masseSalarialeDirect + masseSalarialeIndirect;
  
      const coûtDeRevientParChaine = coûtChargesFixes + coûtAchatPlastiqueFils + coûtEnergie + transportCost + totalMassSalary;
  
      const model = models.find(model => model.id === planningData.model_id);
      const chiffreAffairesParChaine = planningData.qte * model.prixFacture;
  
      const margeBruteNette = chiffreAffairesParChaine - coûtDeRevientParChaine;
      const IS = margeBruteNette * 0.2;
      const margeNetteDeModel = margeBruteNette - IS;
      const totalPrimesParModel = 0;
      const margeNette = margeNetteDeModel - totalPrimesParModel;
  
      const calculatedTaux = margeNette / chiffreAffairesParChaine;
      console.log("📊 Final Calculations:");
      console.log("margeNette:", margeNette);
      console.log("chiffreAffairesParChaine:", chiffreAffairesParChaine);
      console.log("taux:", calculatedTaux);
  
      setTaux(calculatedTaux);
    }

    if (taux > 15) {
      setModalMessage("Le taux est supérieur à 15%");
    } else {
      setModalMessage("Le taux est inférieur ou égal à 15%");
    }
  
    setShowModal(true);
  };
      
  // Schmellow101018!!
  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const sendToAdmin = async () => {
    try {
      await api.post(`/product_plans_admin/${planningDataId}` , {message: "can you accept this plan?" , taux: taux});
      setShowModal(false);
    } catch (error) {
      setModalMessage(error.message);
      setShowModal(false);
    }
  }

  return (
    <div className="md:ml-[16.66%] mr-6 pt-[6rem]">
      <ToastContainer />
      <div className="flex items-center justify-between">
        <h1 className="ml-7 mb-5 text-2xl">
          {isEditing ? "modifier la planification" : "Créer une planification"}
        </h1>
        <div className="flex items-center w-[400px] justify-between">
        {(isEditing || planningData) && 
          <>
            <Button classes={"bg-green-400"} handlePress={handleTest}>
             Test
            </Button>
            <Button classes={"bg-yellow-400"} handlePress={handleEdit}>
              modifier
            </Button>
            <Button classes={"bg-red-400"} handlePress={handleDelete}>
              Supprimer la planification
            </Button>
          </>
          }
        </div>
      </div>
      <div className="ml-7 mb-4">
        <label className="block font-semibold">model_id</label>
        <ModelSelect fullWidth className="mt-4" value={data.model_id} onChange={(e) => onChange("model_id", e.target.value)} models={models} />
      </div>
      <div className="ml-7 mb-4">
        <label className="block font-semibold">Chaîne</label>
        <select
          className="block w-full mt-4 outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded"
          value={data.chain}
          onChange={(e) => onChange("chain", e.target.value)}
        >
          <option value="">Sélectionnez la chaîne</option>
          {chains.map((chain) => (
            <option key={chain.id} value={chain.id}>
              {chain.name}
            </option>
          ))}
        </select>
      </div>
      <div className="ml-7 mb-4">
        <label className="block font-semibold">Date étude</label>
        <FrenchDatePicker
          selected={data.date_etude ? new Date(data.date_etude) : null}
          onChange={(date) => handleDateChange(date, "date_etude")}
          dateFormat="yyyy-MM-dd"
          className="outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded mt-4"
          required
        />
      </div>
      {loading && <p className="text-center font-semibold">Chargement...</p>}

      {!loading && !planningData && (
        <form
          className="ml-7"
          onSubmit={isEditing ? handleUpdateSubmit : handleSubmit}
        >
          {submissionErrors.length > 0 && (
            <div
              className="mb-5 rounded-lg border border-red-300 bg-red-50 px-5 py-4 text-red-800"
              role="alert"
              aria-live="assertive"
            >
              <p className="font-semibold">
                {isEditing ? "Impossible de modifier la planification" : "Impossible de créer la planification"}
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                {submissionErrors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="mb-4">
            <label className="block font-semibold">Date de début</label>
            <FrenchDatePicker
              selected={data.start_date ? new Date(data.start_date) : null}
              onChange={(date) => handleDateChange(date, "start_date")}
              dateFormat="yyyy-MM-dd"
              className="outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded mt-4"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block font-semibold">Date de fin</label>
            <FrenchDatePicker
              selected={data.end_date ? new Date(data.end_date) : null}
              onChange={(date) => handleDateChange(date, "end_date")}
              dateFormat="yyyy-MM-dd"
              className="outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded mt-4"
              required
            />
          </div>

          <Input
            container="mb-4"
            label="Quantité Société"
            type="number"
            name="qte"
            text={data.qte}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Main d’œuvre directe / Gamme"
            type="number"
            name="main_oeuvre_directe_gamme"
            text={data.main_oeuvre_directe_gamme}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Main d’œuvre directe / Support"
            type="number"
            name="main_oeuvre_directe_support"
            text={data.main_oeuvre_directe_support}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Total de la main d’œuvre directe affectée"
            type="number"
            name="total_main_oeuvre_directe_affectee"
            text={data.total_main_oeuvre_directe_affectee}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Rendement prévu par la gamme"
            type="number"
            name="rendement_prevu_gamme"
            text={data.rendement_prevu_gamme}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Valeur de travail"
            type="number"
            name="temps_alloue"
            text={data.temps_alloue}
            handleChange={onChange}
          />

          <label htmlFor="file">Fichier Excel facultatif (.xls ou .xlsx)</label>
          <input id="file" type="file" accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFileChange} />

          <Button classes="bg-blue-500 my-5 mb-8">
            {subLoading
              ? "Updating ..."
              : isEditing
              ? "modifier la planification"
              : "Créer une planification"}
          </Button>
        </form>
      )}

      {planningData && !loading && !error && (
        <div className="ml-7">
          <FullCalendar
            url={"/product_plans_hours"}
            planningData={planningData}
          />
        </div>
      )}

    {showModal && (
        <div className="fixed z-50 inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center">
          <div className="bg-white p-6 rounded shadow-lg">
            <p className="text-xl font-semibold">{modalMessage}</p>
            <div className="flex items-center justify-end">
              {
                taux < 15 && 
                  <Button classes="bg-green-500 mt-5 " container="mr-[12px]" handlePress={sendToAdmin}>Envoyé</Button>
              }
              <Button classes="bg-red-500 mt-5 " handlePress={handleCloseModal}>Fermer</Button>
            </div>
          </div>
        </div>
      )}


      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 z-50 bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-5 rounded">
            <h2 className="text-xl mb-4">
              Êtes-vous sûr de vouloir supprimer ce plan ?
            </h2>
            <div className="flex justify-end">
              <Button classes="bg-red-500 w-[70px]" handlePress={confirmDelete}>
                Supprimer
              </Button>
              <Button classes="bg-gray-300 ml-3" handlePress={cancelDelete}>
                Annuler
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductPlanning;
